import {
  App,
  Button,
  Card,
  Drawer,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CalculatorOutlined, EyeOutlined, FilePdfOutlined, NotificationOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import { academicsApi, type ResultRow } from '@/api/academics';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { downloadFile } from '@/shared/lib/download';

export function ResultsPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCalc = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));
  const canPublish = useAuthStore((s) => s.hasAuthority('ACADEMICS:APPROVE'));
  const academicYearId = useAuthStore((s) => s.academicYearId);
  // Teacher scope — only show class sections they teach.
  const scope = useAuthStore((s) => s.user?.scope);
  const ownedSectionIds = scope && !scope.unbounded
    ? new Set(scope.teacherClassSectionIds)
    : null;

  const [classSectionId, setClassSectionId] = useState<number | undefined>(undefined);
  const [subjectId, setSubjectId] = useState<number | undefined>(undefined);
  const [reportFor, setReportFor] = useState<number | null>(null);

  const { data: allSections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    staleTime: 60_000,
  });
  const sections = ownedSectionIds
    ? (allSections ?? []).filter((s) => ownedSectionIds.has(s.id))
    : allSections;

  const { data: classSubjects } = useQuery({
    queryKey: ['academics', 'class-subjects', classSectionId],
    queryFn: () => academicsApi.listClassSubjects(classSectionId!),
    enabled: classSectionId != null,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['academics', 'results', classSectionId, subjectId],
    queryFn: () => academicsApi.resultsForSection(classSectionId!, subjectId!),
    enabled: classSectionId != null && subjectId != null,
  });

  const calculate = useMutation({
    mutationFn: () => academicsApi.calculateResults(classSectionId!),
    onSuccess: (n) => {
      message.success(`Calculated ${n} subject result rows`);
      qc.invalidateQueries({ queryKey: ['academics', 'results'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const publish = useMutation({
    mutationFn: (publishFlag: boolean) => academicsApi.publishResults(classSectionId!, publishFlag),
    onSuccess: (n, publishFlag) => {
      message.success(`${publishFlag ? 'Published' : 'Unpublished'} ${n} result rows`);
      qc.invalidateQueries({ queryKey: ['academics', 'results'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const allPublished = (data ?? []).length > 0 && data!.every((r) => r.published);
  const anyCalculated = (data ?? []).some((r) => r.percentage != null);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Results & report cards"
        description="Calculate weighted aggregates from saved marks, view per-subject results, and publish report cards."
        actions={
          classSectionId != null ? (
            <Space>
              {canCalc && (
                <Button
                  icon={<CalculatorOutlined />}
                  loading={calculate.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: 'Recalculate results for this section?',
                      content:
                        'Reads saved marks + active weightages + active grading scale. Existing publish state is preserved.',
                      onOk: () => calculate.mutate(),
                    })
                  }
                >
                  Calculate
                </Button>
              )}
              {canPublish && anyCalculated && (
                <Button
                  type="primary"
                  icon={<NotificationOutlined />}
                  loading={publish.isPending}
                  onClick={() => publish.mutate(!allPublished)}
                >
                  {allPublished ? 'Unpublish' : 'Publish'}
                </Button>
              )}
            </Space>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Class section"
          style={{ minWidth: 320 }}
          value={classSectionId}
          onChange={(v) => {
            setClassSectionId(v);
            setSubjectId(undefined);
          }}
          options={(sections ?? []).map((s) => ({
            value: s.id,
            label: `${s.className} · ${s.sectionName} · ${s.academicYearName}`,
          }))}
        />
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Subject"
          style={{ minWidth: 280 }}
          value={subjectId}
          onChange={(v) => setSubjectId(v)}
          disabled={classSectionId == null}
          options={(classSubjects ?? []).map((cs) => ({ value: cs.subjectId, label: cs.subjectName ?? '' }))}
        />
      </Space>

      {classSectionId == null || subjectId == null ? (
        <Empty description="Pick a section and subject to view its result list" />
      ) : data && data.length === 0 && !isFetching ? (
        <Empty description="No active enrollments in this section" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="enrollmentId"
            size="middle"
            loading={isFetching}
            dataSource={data ?? []}
            pagination={false}
            columns={[
              {
                title: 'Pos',
                dataIndex: 'classPosition',
                width: 70,
                align: 'right',
                render: (p: number | null) =>
                  p ? <Tag color="processing">#{p}</Tag> : <span style={{ opacity: 0.4 }}>—</span>,
              },
              { title: 'Reg #', dataIndex: 'registrationNumber', width: 130 },
              { title: 'Student', dataIndex: 'studentName' },
              {
                title: 'Total',
                dataIndex: 'weightedTotal',
                width: 110,
                align: 'right',
                render: (v: string | null) => v != null ? Number(v).toFixed(2) : '—',
              },
              {
                title: 'Percentage',
                dataIndex: 'percentage',
                width: 110,
                align: 'right',
                render: (v: string | null) => v != null ? `${Number(v).toFixed(2)}%` : '—',
              },
              {
                title: 'Grade',
                dataIndex: 'grade',
                width: 90,
                render: (g: string | null) => g ? <Tag color="processing">{g}</Tag> : '—',
              },
              {
                title: 'Pass',
                dataIndex: 'pass',
                width: 90,
                render: (p: boolean | null) =>
                  p == null ? '—' : p ? <Tag color="success">Pass</Tag> : <Tag color="error">Fail</Tag>,
              },
              {
                title: 'Published',
                dataIndex: 'published',
                width: 110,
                render: (b: boolean) => (b ? <Tag color="success">Published</Tag> : <Tag>Draft</Tag>),
              },
              {
                title: '',
                width: 200,
                align: 'right',
                render: (_, row: ResultRow) => (
                  <Space size={0}>
                    <Button
                      size="small"
                      type="text"
                      icon={<EyeOutlined />}
                      onClick={() => setReportFor(row.enrollmentId)}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      type="text"
                      icon={<FilePdfOutlined />}
                      onClick={() =>
                        downloadFile(`/reports/exports/report-card/${row.enrollmentId}.pdf`, {},
                          `report-card-${row.enrollmentId}.pdf`)
                      }
                    >
                      PDF
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      {reportFor != null && <ReportCardModal enrollmentId={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}

function ReportCardModal({ enrollmentId, onClose }: { enrollmentId: number; onClose: () => void }) {
  const { data, isFetching } = useQuery({
    queryKey: ['academics', 'report-card', enrollmentId],
    queryFn: () => academicsApi.reportCard(enrollmentId),
  });

  return (
    <Drawer open onClose={onClose} title="Report card" size={620}>
      {isFetching ? (
        <Modal open={false} />
      ) : data ? (
        <Space orientation="vertical" style={{ width: '100%' }} size={14}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {data.studentName}
            </Typography.Title>
            <Typography.Text type="secondary">
              {data.registrationNumber} · {data.classSectionName} · {data.academicYearName}
            </Typography.Text>
          </div>

          <Card>
            <Space wrap size={[14, 4]}>
              <Typography.Text strong>Aggregate</Typography.Text>
              <Tag color="processing" style={{ fontSize: 13 }}>
                {data.aggregatePercentage != null ? `${Number(data.aggregatePercentage).toFixed(2)}%` : '—'}
              </Tag>
              {data.aggregateGrade && (
                <Tag color="processing" style={{ fontSize: 13 }}>
                  {data.aggregateGrade}
                </Tag>
              )}
              {data.overallPass != null &&
                (data.overallPass ? (
                  <Tag color="success">Overall pass</Tag>
                ) : (
                  <Tag color="error">Overall fail</Tag>
                ))}
              {data.allPublished ? (
                <Tag color="success">Published</Tag>
              ) : (
                <Tag>Draft</Tag>
              )}
            </Space>
            {data.calculatedAt && (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                Last calculated {dayjs(data.calculatedAt).format('D MMM YYYY · HH:mm')}
              </Typography.Text>
            )}
          </Card>

          <Card styles={{ body: { padding: 0 } }}>
            <Table
              rowKey="subjectId"
              size="small"
              dataSource={data.subjects}
              pagination={false}
              columns={[
                { title: 'Subject', dataIndex: 'subjectName' },
                {
                  title: 'Total',
                  dataIndex: 'weightedTotal',
                  width: 90,
                  align: 'right',
                  render: (v: string | null) => v != null ? Number(v).toFixed(2) : '—',
                },
                {
                  title: '%',
                  dataIndex: 'percentage',
                  width: 90,
                  align: 'right',
                  render: (v: string | null) => v != null ? `${Number(v).toFixed(2)}%` : '—',
                },
                {
                  title: 'Grade',
                  dataIndex: 'grade',
                  width: 80,
                  render: (g: string | null) => g ? <Tag color="processing">{g}</Tag> : '—',
                },
                {
                  title: 'Pos',
                  dataIndex: 'classPosition',
                  width: 70,
                  align: 'right',
                  render: (p: number | null) => p ? `#${p}` : '—',
                },
                {
                  title: 'Pass',
                  dataIndex: 'pass',
                  width: 90,
                  render: (p: boolean | null) =>
                    p == null ? '—' : p ? <Tag color="success">Pass</Tag> : <Tag color="error">Fail</Tag>,
                },
              ]}
            />
          </Card>
        </Space>
      ) : null}
    </Drawer>
  );
}
